import { useState, useRef, useEffect, useCallback } from 'react'
import { Sparkles, X, Send, Loader2, FileCode, ChevronDown, Maximize2, Minimize2 } from 'lucide-react'
import { getAPIBaseUrl } from '../../environment'
import './AIAssistant.css'

// ── Page-specific quick action suggestions ───────────────────────────────────

const PAGE_SUGGESTIONS = {
  projects: [
    { label: 'What is a project?',        prompt: 'What is a project on this page and how do I use one?' },
    { label: 'How do I start?',           prompt: 'How do I start a transformation for a project?' },
    { label: 'Explain this page',         prompt: 'What does this Projects page show and what can I do here?' },
    { label: 'Accounts & projects',       prompt: 'How do accounts and projects relate to each other?' },
  ],
  runs: [
    { label: 'What is a run?',            prompt: 'What is a transformation run?' },
    { label: 'Explain run statuses',      prompt: 'What do the different run statuses mean — running, completed, failed?' },
    { label: 'What should I open next?',  prompt: 'What should I do next from this Runs page?' },
    { label: 'How do reports work?',      prompt: 'How do I use Reports for a completed run?' },
  ],
  workflow: [
    { label: 'Explain this stage',        prompt: 'What does the current stage do and what should I expect from it?' },
    { label: 'What do I do next?',        prompt: 'What is the next step in the workflow and how do I proceed?' },
    { label: 'Explain this error',        prompt: 'The current stage has an error. Can you explain what went wrong and what I should do?' },
    { label: 'Summarize results',         prompt: 'Summarize what has been completed so far and what results are available.' },
  ],
  reports: [
    { label: 'Explain this report',       prompt: 'What does the current report show and how should I interpret it?' },
    { label: 'What do these metrics mean?', prompt: 'Explain the key metrics and findings shown in this report.' },
    { label: 'What should I look at next?', prompt: 'Which report or section should I look at next?' },
    { label: 'Summarize this run',        prompt: 'Summarize the results available for the selected run.' },
  ],
  analyze_dashboard: [
    { label: 'What do these cards show?',   prompt: 'Explain the summary cards on the Analyze Dashboard — what does each metric mean for my modernization project?' },
    { label: 'What are entry points?',      prompt: 'What are execution entry points in a COBOL system and why do they matter for the transformation?' },
    { label: 'What is the data layer?',     prompt: 'Explain the data layer section — what are VSAM files, DB2 tables, and data assets, and how do they affect modernization scope?' },
    { label: 'What should I review first?', prompt: 'Based on what is shown on the Dashboard, what should I review or check before starting the transformation pipeline?' },
  ],
  analyze_inventory: [
    { label: 'How are files classified?',    prompt: 'How are files classified in the Inventory? What do COBOL, Copybook, JCL, BMS, SQL, and VSAM groups mean?' },
    { label: 'What are line counts for?',    prompt: 'What do the line count and LOC metrics in the Inventory table mean for understanding the size of this transformation?' },
    { label: 'What is the AI Actions menu?', prompt: 'What can I do with the AI Actions button on each file in the Inventory? What are the available actions?' },
    { label: 'What should I focus on?',      prompt: 'I am looking at the Inventory table. What should I focus on before confirming the pipeline?' },
  ],
  analyze_technical: [
    { label: 'Explain dependencies',       prompt: 'What does the Dependencies section in Technical View show and why does the call graph matter for transformation planning?' },
    { label: 'Explain copybook usage',     prompt: 'What does the Copybooks section show? How do shared copybooks affect the scope and complexity of a modernization?' },
    { label: 'What are the issues?',       prompt: 'Explain the Issues section in Technical View — what kinds of issues are flagged and what should I do about them?' },
    { label: 'What do statistics mean?',   prompt: 'What do the Statistics in Technical View tell me about the complexity and risk of this COBOL-to-Java transformation?' },
  ],
}

// ── Page-specific empty state labels ────────────────────────────────────────

const PAGE_LABELS = {
  projects: {
    heading: 'Projects Help',
    sub: 'Ask about projects, accounts, or how to get started with a transformation.',
  },
  runs: {
    heading: 'Runs Help',
    sub: 'Ask about run statuses, reports, or what to do next.',
  },
  workflow: {
    heading: 'Workflow Help',
    sub: 'Ask about the current stage, errors, next steps, or available results.',
  },
  reports: {
    heading: 'Reports Help',
    sub: 'Ask about the current report, metrics, or what to look at next.',
  },
  analyze_dashboard: {
    heading: 'Analyze — Dashboard Help',
    sub: 'Ask about summary cards, execution entry points, the data layer, or what to review next.',
  },
  analyze_inventory: {
    heading: 'Analyze — Inventory Help',
    sub: 'Ask about file classifications, line counts, the file preview pane, or AI actions.',
  },
  analyze_technical: {
    heading: 'Analyze — Technical View Help',
    sub: 'Ask about dependencies, copybooks, data assets, JCL execution, issues, or statistics.',
  },
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function deriveStageStatus(nodeResult) {
  if (!nodeResult) return 'pending'
  if (nodeResult.success === false) return 'failed'
  if (nodeResult.success === true || nodeResult.status === 'complete') return 'complete'
  if (nodeResult.status === 'running' || nodeResult.status === 'in-progress') return 'running'
  return 'complete'
}

function getAuthHeader() {
  const token = localStorage.getItem('modernizeit_token')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

// ── Component ─────────────────────────────────────────────────────────────────

function AIAssistant({
  account,
  application,
  run,
  selectedNode,
  executionResults = {},
  pageType = 'workflow',
  pageContext = {},
}) {
  const apiBaseUrl = getAPIBaseUrl()

  const [isOpen, setIsOpen] = useState(false)
  const [isDetached, setIsDetached] = useState(false)
  const [position, setPosition] = useState({ x: 100, y: 100 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [contextExpanded, setContextExpanded] = useState(false)
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)
  const panelRef = useRef(null)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 100)
  }, [isOpen])

  // Clear messages when page type changes (navigating between pages)
  useEffect(() => {
    setMessages([])
    setInput('')
  }, [pageType])

  // Dragging — detached mode only
  const handleHeaderMouseDown = useCallback((e) => {
    if (!isDetached) return
    if (e.target.closest('button')) return
    setIsDragging(true)
    const rect = panelRef.current?.getBoundingClientRect()
    if (rect) setDragOffset({ x: e.clientX - rect.left, y: e.clientY - rect.top })
    e.preventDefault()
  }, [isDetached])

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return
    setPosition({
      x: Math.max(0, Math.min(window.innerWidth - 420, e.clientX - dragOffset.x)),
      y: Math.max(0, Math.min(window.innerHeight - 100, e.clientY - dragOffset.y)),
    })
  }, [isDragging, dragOffset])

  const handleMouseUp = useCallback(() => setIsDragging(false), [])

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', handleMouseUp)
      return () => {
        window.removeEventListener('mousemove', handleMouseMove)
        window.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [isDragging, handleMouseMove, handleMouseUp])

  const toggleDetached = () => {
    if (!isDetached) {
      setPosition({
        x: Math.max(50, (window.innerWidth - 420) / 2),
        y: Math.max(50, (window.innerHeight - 600) / 2),
      })
    }
    setIsDetached(!isDetached)
  }

  // Derive workflow stage context (workflow page only)
  const getCurrentStageContext = () => {
    if (!selectedNode || pageType !== 'workflow') return {}
    const nodeType = selectedNode.data?.nodeType || null
    const nodeResult = executionResults[selectedNode.id]
    const status = deriveStageStatus(nodeResult)
    const error = nodeResult?.error || nodeResult?.message || null
    return { nodeType, status, error }
  }

  // Build completed-nodes summary for workflow context
  const getCompletedNodes = () => {
    if (pageType !== 'workflow') return []
    return Object.entries(executionResults)
      .filter(([, result]) => result?.success !== false)
      .map(([nodeId, result]) => ({
        id: nodeId,
        type: result.node_type || 'unknown',
        hasArtifacts: !!result.artifacts_path,
      }))
  }

  const handleSend = async () => {
    if (!input.trim() || isLoading) return

    const userMessage = input.trim()
    setInput('')
    setMessages(prev => [...prev, { role: 'user', content: userMessage }])
    setIsLoading(true)

    try {
      const { nodeType, status, error } = getCurrentStageContext()
      const completedNodes = getCompletedNodes()

      const requestBody = {
        message: userMessage,
        scout_account_id: String(account || ''),
        application_name: String(application || ''),
        page_type: pageType,
        page_context: pageContext || {},
        run_id: run?.workflow_run_id || null,
        current_stage: nodeType || null,
        stage_status: status || null,
        stage_error: error || null,
        context: {
          selected_node: selectedNode ? {
            id: selectedNode.id,
            type: selectedNode.data?.nodeType,
            label: selectedNode.data?.label,
          } : null,
          completed_nodes: completedNodes,
        },
      }

      const response = await fetch(`${apiBaseUrl}/ai/assistant`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader(),
        },
        body: JSON.stringify(requestBody),
      })

      if (!response.ok) {
        let errorDetail = `API error: ${response.status}`
        try {
          const errData = await response.json()
          if (errData.detail) {
            errorDetail = typeof errData.detail === 'string' ? errData.detail : JSON.stringify(errData.detail)
          }
        } catch (_) {}
        throw new Error(errorDetail)
      }

      const data = await response.json()
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.response,
        files_referenced: data.files_referenced || [],
      }])

    } catch (error) {
      console.error('[WorkspaceAssistant] error:', error)
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Something went wrong: ${error.message}`,
        isError: true,
      }])
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const suggestions = PAGE_SUGGESTIONS[pageType] || PAGE_SUGGESTIONS.workflow
  const labels = PAGE_LABELS[pageType] || PAGE_LABELS.workflow
  const completedNodes = getCompletedNodes()
  const { nodeType: currentNodeType, status: currentStatus } = getCurrentStageContext()

  // Context bar display line
  const contextLine = application
    ? (pageType === 'reports' && run?.workflow_run_id)
      ? `${application} — ${run.workflow_run_id.slice(-10)}`
      : application
    : account
      ? `Account ${account}`
      : 'No project selected'

  return (
    <>
      {/* Floating trigger button */}
      <button
        className={`ai-assistant-trigger ${isOpen ? 'hidden' : ''}`}
        onClick={() => setIsOpen(true)}
        title="Workspace Assistant"
      >
        <Sparkles size={20} />
        <span>AI</span>
      </button>

      {/* Panel — docked or detached */}
      <div
        ref={panelRef}
        className={`ai-assistant-panel ${isOpen ? 'open' : ''} ${isDetached ? 'detached' : ''} ${isDragging ? 'dragging' : ''}`}
        style={isDetached ? { left: position.x, top: position.y } : undefined}
      >
        {/* Header */}
        <div
          className={`ai-assistant-header ${isDetached ? 'draggable' : ''}`}
          onMouseDown={handleHeaderMouseDown}
        >
          <div className="ai-assistant-title">
            <Sparkles size={18} />
            <span>Workspace Assistant</span>
          </div>
          <div className="ai-header-actions">
            <button
              className="ai-header-btn"
              onClick={toggleDetached}
              title={isDetached ? 'Dock panel' : 'Detach panel'}
            >
              {isDetached ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </button>
            <button className="ai-header-btn" onClick={() => setIsOpen(false)}>
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Context bar */}
        <div className="ai-context-bar">
          <button
            className="ai-context-toggle"
            onClick={() => setContextExpanded(!contextExpanded)}
          >
            <FileCode size={14} />
            <span>{contextLine}</span>
            {completedNodes.length > 0 && (
              <span className="context-badge">{completedNodes.length} done</span>
            )}
            {currentStatus && currentStatus !== 'pending' && (
              <span className={`context-badge context-badge--${currentStatus}`}>
                {currentStatus}
              </span>
            )}
            <ChevronDown size={14} className={contextExpanded ? 'rotated' : ''} />
          </button>

          {contextExpanded && (
            <div className="ai-context-details">
              <div className="context-item">
                <span className="context-label">Page:</span>
                <span>{labels.heading}</span>
              </div>
              {application && (
                <div className="context-item">
                  <span className="context-label">Project:</span>
                  <span>{application}</span>
                </div>
              )}
              {run?.workflow_run_id && (
                <div className="context-item">
                  <span className="context-label">Run:</span>
                  <span className="context-value-mono">{run.workflow_run_id.slice(-12)}</span>
                </div>
              )}
              {currentNodeType && (
                <div className="context-item">
                  <span className="context-label">Stage:</span>
                  <span>{currentNodeType}</span>
                </div>
              )}
              {completedNodes.length > 0 && (
                <div className="context-item">
                  <span className="context-label">Completed:</span>
                  <span>{completedNodes.length} stage{completedNodes.length !== 1 ? 's' : ''}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Messages */}
        <div className="ai-messages">
          {messages.length === 0 && (
            <div className="ai-empty-state">
              <Sparkles size={32} />
              <h4>{labels.heading}</h4>
              <p>{labels.sub}</p>
              <div className="ai-suggestions">
                {suggestions.map((s, idx) => (
                  <button key={idx} onClick={() => setInput(s.prompt)}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, idx) => (
            <div key={idx} className={`ai-message ${msg.role} ${msg.isError ? 'error' : ''}`}>
              <div className="ai-message-content">
                {msg.content}
              </div>
              {msg.files_referenced?.length > 0 && (
                <div className="ai-message-files">
                  <span>From:</span>
                  {msg.files_referenced.map((f, i) => (
                    <span key={i} className="file-tag">{f}</span>
                  ))}
                </div>
              )}
            </div>
          ))}

          {isLoading && (
            <div className="ai-message assistant loading">
              <Loader2 size={16} className="spin" />
              <span>Thinking...</span>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="ai-input-area">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={`Ask about ${labels.heading.toLowerCase()}…`}
            rows={2}
            disabled={isLoading}
          />
          <button
            className="ai-send-btn"
            onClick={handleSend}
            disabled={!input.trim() || isLoading}
          >
            <Send size={18} />
          </button>
        </div>
      </div>

      {/* Backdrop — docked mode only */}
      {isOpen && !isDetached && (
        <div className="ai-backdrop" onClick={() => setIsOpen(false)} />
      )}
    </>
  )
}

export default AIAssistant
